import { useTranslation } from "react-i18next";
import { RotateCw } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { AddIcon } from "@/components/icons/action-icons";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAppSelector } from "@/hooks/useReduxHooks";
import { selectUser } from "@/store/slices/user.slice";
import {
  useGetCompanyMineQuery,
  useGetEmployeeCompanyCurrentQuery,
} from "@/services/(my-space)/companiesApi";

import { CompanyCard } from "./CompanyCard";
import { SectionLabel } from "./SectionLabel";
import { useCompanySwitch } from "../use-company-switch";
import { ENUM_ADD_COMPANY_QUERY_PARAM } from "../add-company-dialog";

const SKELETON_ROWS = 2;

/**
 * The companies the signed-in user belongs to, with the active one pinned
 * first. Super admins get switch controls and an "add company" entry point;
 * everyone else sees the list read-only.
 */
export const CompaniesSection = () => {
  const { t } = useTranslation(["common"]);
  const navigate = useNavigate();
  const { user } = useAppSelector(selectUser);
  const isCompanySuperAdmin = user?.isCompanySuperAdmin ?? false;

  // Two endpoints, one response shape: super admins see every company they own,
  // staff see only their current one. Both hooks always run (order stays fixed
  // even if the role flips mid-session) and `skip` keeps the unused one from
  // hitting the network.
  const ownedCompanies = useGetCompanyMineQuery(undefined, {
    skip: !isCompanySuperAdmin,
    refetchOnMountOrArgChange: true,
  });
  const currentCompany = useGetEmployeeCompanyCurrentQuery(undefined, {
    skip: isCompanySuperAdmin,
    refetchOnMountOrArgChange: true,
  });
  const { data, isLoading, isError, refetch } = isCompanySuperAdmin
    ? ownedCompanies
    : currentCompany;

  const { switchTo, switchingCompanyId, isSwitching } = useCompanySwitch();
  const companies = data?.data ?? [];

  return (
    <section className="space-y-2">
      <SectionLabel
        action={
          isCompanySuperAdmin && (
            <Button
              size="xs"
              variant="ghost"
              className="shrink-0 text-primary transition-all hover:bg-primary-muted active:scale-95"
              onClick={() =>
                navigate(
                  `?${ENUM_ADD_COMPANY_QUERY_PARAM.name}=${ENUM_ADD_COMPANY_QUERY_PARAM.value}`,
                  { replace: true },
                )
              }
            >
              <AddIcon size={13} strokeWidth={3} aria-hidden />
              {t("common:chrome.add_company")}
            </Button>
          )
        }
      >
        {t("common:chrome.your_companies")}
        {companies.length > 1 && (
          <span className="ms-1.5 tabular-nums">({companies.length})</span>
        )}
      </SectionLabel>

      {isLoading ? (
        <div className="space-y-2" aria-busy>
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <div
              key={i}
              className="flex items-center gap-3 rounded-sm border border-border p-2.5"
            >
              <Skeleton className="size-10 rounded-xs" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-2.5 w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-2 rounded-sm border border-dashed border-border px-3 py-5 text-center">
          <p className="text-xs text-muted-foreground">
            {t("common:chrome.couldnt_load_your_companies")}
          </p>
          <Button size="xs" variant="outline" onClick={() => refetch()}>
            <RotateCw size={12} aria-hidden />
            {t("common:buttons.retry")}
          </Button>
        </div>
      ) : companies.length ? (
        <ul className="flex flex-col gap-2">
          {companies.map((company) => (
            <CompanyCard
              key={company.id}
              company={company}
              canSwitch={isCompanySuperAdmin}
              isSwitchingThis={switchingCompanyId === company.id}
              isSwitchingAny={isSwitching}
              onSwitch={switchTo}
            />
          ))}
        </ul>
      ) : (
        <p className="rounded-sm border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">
          {t("common:chrome.no_companies_found")}
        </p>
      )}
    </section>
  );
};
